import React from 'react';
import { styled } from '@compiled/react';
import {
	useHasCardExtraFields,
	useShowCompactMode,
} from '@atlassian/jira-software-view-settings/src/controllers/index.tsx';
import {
	CARD_ROW_HEIGHT,
	COMPACT_CARD_ROW_HEIGHT,
	EXTRA_CARD_ROW_HEIGHT,
} from '../../../common/constants/index.tsx';
import { isViewSettingAsPanelExpEnabledWithNoExposure } from '../../../feature-flags.tsx';
import type { CardEntryGroup } from '../../../model/types.tsx';
import { useBacklogSelector } from '../../../state/index.tsx';
import { hasCardExtraFields } from '../../../state/selectors/cards/index.tsx';
import CardContainer from '../card-container/index.tsx';
import type { Props as CardWithIccProps } from '../card-with-icc-wrapper/index.tsx';

export type CardGroupProps = Omit<CardWithIccProps, 'cardId'> & {
	cardGroup: CardEntryGroup;
	cardListId: string;
	isVisible: boolean;
	isScrolling: boolean;
	setRichActiveCard: (cardId: number | null) => void;
};

export function CardGroup({
	cardGroup: { cardIds, isAttachedToParent, isCollapsed },
	isVisible,
	isScrolling,
	cardListId,
	setRichActiveCard,
}: CardGroupProps) {
	const isCompactMode = useShowCompactMode();

	/**
	 * Checking if the card has extra fields set from board configuration
	 */
	const hasExtraFields = useBacklogSelector(hasCardExtraFields);
	/**
	 * Checking if the extra fields is off from view settings
	 */
	const shouldRenderExtraFields = useHasCardExtraFields();
	const hasExtraCardHeight = isViewSettingAsPanelExpEnabledWithNoExposure()
		? shouldRenderExtraFields && hasExtraFields
		: hasExtraFields;

	let cardRowHeight: number = isCompactMode ? COMPACT_CARD_ROW_HEIGHT : CARD_ROW_HEIGHT;
	cardRowHeight = hasExtraCardHeight ? cardRowHeight + EXTRA_CARD_ROW_HEIGHT : cardRowHeight;

	const content = !isCollapsed
		? cardIds.map((cardId, index) => {
				const cardContainerEl = (
					<CardContainer
						isVisible={isVisible}
						isScrolling={isScrolling}
						cardId={cardId}
						cardListId={cardListId}
						isAttachedToParent={isAttachedToParent}
						setRichActiveCard={setRichActiveCard}
					/>
				);

				return (
					<CardWrapper
						key={cardId}
						// eslint-disable-next-line jira/react/no-style-attribute
						style={{
							top: index * cardRowHeight,
						}}
					>
						{cardContainerEl}
					</CardWrapper>
				);
			})
		: null;

	return <CardGroupWrapper>{content}</CardGroupWrapper>;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardGroupWrapper = styled.div({
	position: 'relative',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardWrapper = styled.div({
	position: 'absolute',
	width: '100%',
});
